/**
 * src/hooks/useProjectsFiltering.js — Filter state + derived data for ProjectsView
 *
 * Extracted from ProjectsView.jsx to reduce component size.
 * All filter-related useState, useMemo, and helper functions live here.
 */

import { useState, useMemo, useEffect } from 'react';
import { normalizeTaskStatus } from '../lib/constants/taskStatus';
import { applyContract } from '../lib/contracts/renderContract';
import { reportRenderAudit } from '../lib/observability/renderAudit';

// FIX[shape]: project_types is flat snake_case. Display label = label_en || type_name
const projectTypeName = (pt) => pt?.label_en || pt?.type_name || pt?.slug || '';

// FIX[shape]: project rows are flat snake_case with joined `stages` and `project_types`
const projectStageName = (p) => p.stages?.label_en || p.stages?.stage_name || '';
const projectTypeNameFor = (p, projectTypeLookup) =>
  projectTypeName(p.project_types) || projectTypeLookup[p.project_type_id] || '';

// FIX[shape]: project_type tag derived from joined project_types row.
// vessel_id is now scalar (not Airtable array). Subproject detection is best-effort
// until a dedicated `is_subproject` column lands (TODO[wave-2]).
const isSubprojectType = (p) => {
  const slug = (p.project_types?.slug || p.project_types?.label_en || '').toLowerCase();
  return slug.includes('subproyecto') || slug.includes('subproject');
};

// ── Project lifecycle: active work vs inactive (legacy/closed) history ──
// CEO 2026-06-11: closed/legacy boats clutter the active pipeline + sales views.
// They STAY in the system (Chroma/brain learning + FK cross-references) but are
// hidden from the active board by default and reachable via the lifecycle filter
// ("off by default, but searchable"). Status values are append-only (lib/db enums).
const INACTIVE_LIFECYCLE_STATUSES = new Set(['legacy', 'closed', 'completed', 'archived']);

// Terminal kanban stages: a project parked here is finished work, so it must be
// soft-archived off the active board even while its `status` column still reads
// 'active'. CEO 2026-06-21 bug report: "if a project is finalized/closed it
// should be soft-archived — retrievable in the legacy tab but not on the active
// list." Stage names are the canonical English labels from the `stages` table
// (label_en: "Finalized" / "Closed"). Reachable again via the legacy/all filter,
// and auto-restored to the active board if the stage is moved back.
const TERMINAL_STAGES = new Set(['finalized', 'closed']);

const projectStageLabel = (p) =>
  String(p?.stages?.label_en || p?.stages?.stage_name || '').trim().toLowerCase();

export function isInactiveLifecycle(project) {
  if (INACTIVE_LIFECYCLE_STATUSES.has(String(project?.status || '').toLowerCase())) return true;
  return TERMINAL_STAGES.has(projectStageLabel(project));
}

/**
 * Lifecycle filter predicate for the Projects board.
 * @param {object} project - project row (reads `status`)
 * @param {'active'|'legacy'|'all'} [lifecycle='active'] - default hides inactive
 * @returns {boolean} whether the project is visible under this filter
 */
export function matchesLifecycle(project, lifecycle = 'active') {
  if (lifecycle === 'all') return true;
  const inactive = isInactiveLifecycle(project);
  return lifecycle === 'legacy' ? inactive : !inactive;
}

/**
 * Search predicate for a project card (TICKET-531).
 *
 * Same class as the pipeline search bug: the haystack must carry every name the
 * card shows — owner and vessel included. Both joins already ride on
 * SELECT_PROJECT (src/lib/api/projects.js), they were just never searched, so
 * typing a person's or a boat's name emptied the board.
 */
export function projectMatchesSearch(project, query) {
  const q = String(query || '').toLowerCase().trim();
  if (!q) return true;
  const hay = [
    project.project_name,
    project.short_description,
    project.vessels?.vessel_name,
    project.user_profiles?.display_name,
  ].filter(Boolean).map(s => String(s).toLowerCase()).join(' ');
  return hay.includes(q);
}

export default function useProjectsFiltering({
  projects: rawProjects,
  stages,
  flowMap,
  projectTypes,
  projectTypeLookup,
  bundles: rawBundles,
  getViewFilters,
  setViewFilter,
  clearViewFilters,
}) {
  // ── Phase B2: render contract gates before view-specific filtering ──
  const { visible: projects } = useMemo(
    () => applyContract('projects', 'project', rawProjects || []),
    [rawProjects],
  );
  const { visible: bundles } = useMemo(
    () => applyContract('projects', 'bundle', rawBundles || []),
    [rawBundles],
  );

  // ── Filter persistence (projects view) ─────────────────
  const persistedFilters = getViewFilters('projects');
  const [search, setSearch] = useState(persistedFilters.search || '');
  const [stageFilter, setStageFilter] = useState(persistedFilters.stage || 'All');
  const [typeFilter, setTypeFilter] = useState(persistedFilters.projectType || 'All');
  const [statusFilter, setStatusFilter] = useState(persistedFilters.status || '');
  const [assigneeFilter, setAssigneeFilter] = useState(persistedFilters.assignee || '');
  const [projectFilter, setProjectFilter] = useState(persistedFilters.project || '');
  // TICKET-714 (Agus): find all projects done to one boat. Exact vessel_id match —
  // unlike search, this is not a substring and pairs with lifecycle 'all' so
  // finished projects show (the view widens lifecycle when a vessel is picked).
  const [vesselFilter, setVesselFilter] = useState(persistedFilters.vessel || '');
  // Lifecycle: 'active' (default) | 'legacy' | 'all'. Keeps closed/legacy boats
  // out of the active board until explicitly requested.
  const [lifecycleFilter, setLifecycleFilter] = useState(persistedFilters.lifecycle || 'active');

  const typeFilterOptions = useMemo(() => {
    const names = projectTypes
      .map(projectTypeName)
      .filter(n => n && !n.toLowerCase().includes('subproyecto') && !n.toLowerCase().includes('subproject'));
    return ['All', ...new Set(names)];
  }, [projectTypes]);

  // Find the stage flow linked to a project type filter (data-driven via project_type_ids)
  const findFlowForType = (filter) => {
    if (!filter || filter === 'All') return null;
    const q = filter.toLowerCase();

    const matchingType = projectTypes.find(pt => {
      const name = projectTypeName(pt).toLowerCase();
      return name === q || name.includes(q) || q.includes(name);
    });
    const typeId = matchingType?.id;

    if (typeId) {
      for (const [flowId, flow] of Object.entries(flowMap)) {
        // Only match project flows — CRM flows have their own stage set
        if (flow.flow_type === 'project' && (flow.project_type_ids || []).includes(typeId)) {
          return { ...flow, flowId };
        }
      }
    }

    // Fallback: match by flow name against the filter string
    for (const [flowId, flow] of Object.entries(flowMap)) {
      const flowName = (flow.name || flow.slug || '').toLowerCase();
      if (flowName && (flowName.includes(q) || q.includes(flowName))) {
        return { ...flow, flowId };
      }
    }

    return null;
  };

  const stageNames = useMemo(() => {
    if (typeFilter === 'All') {
      const all = new Set();
      // FIX 2026-04-08: filter out CRM flows. Without this, Lead/Contacted/
      // Quoted/Negotiating/Won/Lost/Cancelled stages from the crm flow leaked
      // into the Projects view kanban columns and filter dropdown. Projects
      // view only cares about production stages (flow_type='project').
      Object.values(flowMap).filter(f => f.flow_type === 'project').forEach(f => f.steps.forEach(s => all.add(s)));
      return ['All', ...all];
    }
    const matchingFlow = findFlowForType(typeFilter);
    if (matchingFlow?.steps.length) {
      return ['All', ...matchingFlow.steps];
    }
    // FIX[shape]: stages now flat snake_case
    return ['All', ...stages.map(s => s.label_en || s.stage_name).filter(Boolean)];
  }, [typeFilter, flowMap, stages, projectTypes]);

  const bundleLookup = useMemo(() => {
    const map = {};
    bundles.forEach(b => { if (b.id) map[b.id] = b.title || b.name || 'Unknown Bundle'; });
    return map;
  }, [bundles]);

  // Build sub-project map: { parentProjectId -> [subProjects] }
  const subProjectMap = useMemo(() => {
    const map = {};
    const subProjects = projects.filter(isSubprojectType);
    const parentProjects = projects.filter(p => !isSubprojectType(p));

    subProjects.forEach(sub => {
      const subName = (sub.project_name || '').toLowerCase();
      const subVesselId = sub.vessel_id;
      let bestParent = null;
      let bestScore = 0;
      parentProjects.forEach(parent => {
        let score = 0;
        if (subVesselId && parent.vessel_id && subVesselId === parent.vessel_id) score += 10;
        const parentName = (parent.project_name || '').toLowerCase();
        if (parentName && subName.includes(parentName.split(' ')[0])) score += 3;
        if (parentName && parentName.includes(subName.split(' ')[0])) score += 2;

        if (score > bestScore) { bestScore = score; bestParent = parent; }
      });
      if (bestParent && bestScore >= 2) {
        if (!map[bestParent.id]) map[bestParent.id] = [];
        map[bestParent.id].push(sub);
      }
    });
    return map;
  }, [projects]);

  const filtered = useMemo(() => {
    return projects.filter(p => {
      // Always surface optimistic pending rows so the user sees their
      // in-flight project (Bug 1 fix: the ghost previously vanished under
      // any stage filter because stage_id was null, leaving the user with
      // zero feedback that a save was attempted).
      if (p._pending) return true;
      // Lifecycle gate: hide legacy/closed boats from the active board unless the
      // filter explicitly asks for them (off by default, reachable on demand).
      if (!matchesLifecycle(p, lifecycleFilter)) return false;
      if (stageFilter !== 'All') {
        const projStage = projectStageName(p).trim().toLowerCase();
        const filterStage = stageFilter.trim().toLowerCase();
        if (projStage !== filterStage) return false;
      }
      if (typeFilter !== 'All') {
        const typeName = projectTypeNameFor(p, projectTypeLookup);
        if (typeName !== typeFilter) return false;
      }
      // Exclude subprojects from main list (they appear nested under parents)
      if (isSubprojectType(p)) return false;
      if (!projectMatchesSearch(p, search)) return false;
      if (projectFilter && p.id !== projectFilter) return false;
      if (vesselFilter && p.vessel_id !== vesselFilter) return false;
      if (assigneeFilter) {
        if (p.owner_id !== assigneeFilter) return false;
      }
      if (statusFilter) {
        const projStage = projectStageName(p).toLowerCase();
        // normalizeTaskStatus maps both legacy labels ('to do') and slugs ('todo') -> canonical slug
        const filterSlug = normalizeTaskStatus(statusFilter);
        if (filterSlug === 'todo') {
          if (!['scan', 'design', 'materials'].includes(projStage) && projStage !== '') return false;
        } else if (filterSlug === 'in_progress') {
          if (!['cutting', 'manufacturing', 'production', 'fitting', 'adjustments'].includes(projStage)) return false;
        } else if (filterSlug === 'blocked') {
          return false;
        } else if (filterSlug === 'done') {
          if (!['quality control', 'delivery', 'closed'].includes(projStage)) return false;
        } else if (projStage !== filterSlug) {
          return false;
        }
      }
      return true;
    });
  }, [projects, stageFilter, typeFilter, search, statusFilter, assigneeFilter, projectFilter, vesselFilter, lifecycleFilter, projectTypeLookup]);

  // Vessel options for the filter dropdown — derived from the projects themselves
  // (vessel join already on SELECT_PROJECT), so only boats that HAVE projects list.
  const vesselFilterOptions = useMemo(() => {
    const map = new Map();
    projects.forEach(p => {
      if (p.vessel_id && p.vessels?.vessel_name && !map.has(p.vessel_id)) {
        map.set(p.vessel_id, p.vessels.vessel_name);
      }
    });
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [projects]);

  // ── Phase A2: render reconciliation audit ─────────────────
  // requested = raw projects; returned = post-contract; rendered = post-filter.
  useEffect(() => {
    const requested = rawProjects?.length || 0;
    const returned = projects?.length || 0;
    const rendered = filtered?.length || 0;
    const excluded = Math.max(0, requested - rendered);
    reportRenderAudit({ view: 'projects', requested, returned, rendered, excluded });
  }, [rawProjects, projects, filtered]);

  return {
    search, setSearch,
    stageFilter, setStageFilter,
    typeFilter, setTypeFilter,
    statusFilter, setStatusFilter,
    assigneeFilter, setAssigneeFilter,
    projectFilter, setProjectFilter,
    vesselFilter, setVesselFilter,
    vesselFilterOptions,
    lifecycleFilter, setLifecycleFilter,
    filtered,
    stageNames,
    typeFilterOptions,
    findFlowForType,
    subProjectMap,
    bundleLookup,
    // Re-export helpers for components that need them
    projectTypeName,
    projectStageName,
    projectTypeNameFor,
    isSubprojectType,
  };
}

export { projectTypeName, isSubprojectType };
